import { useState } from 'react';
import { polygon } from 'wagmi/chains';
import { truncateDecimals } from '../utils/formatters';

/**
 * Hook for managing bridge form state
 * Handles source chain selection and amount input
 */
export const useFormState = () => {
  // Source chain (Polygon or Ethereum)
  const [sourceChainId, setSourceChainId] = useState<number>(polygon.id);

  // Bridge amount (as string for input handling)
  const [amount, setAmount] = useState<string>('');

  // Transaction hash for tracking
  const [txHash, setTxHash] = useState<`0x${string}` | undefined>(undefined);

  /**
   * Clear all form inputs
   */
  const clearForms = () => {
    setAmount('');
    setTxHash(undefined);
  };

  /**
   * Set amount with validation and 6-decimal truncation
   * Bridge only supports 6 decimals, so we truncate input gently
   * @param value - Amount string
   */
  const setValidatedAmount = (value: string) => {
    // Allow empty string
    if (value === '') {
      setAmount('');
      return;
    }

    // Only allow numbers and single decimal point
    const regex = /^\d*\.?\d*$/;
    if (regex.test(value)) {
      // Truncate to 6 decimals max (bridge constraint)
      const truncated = truncateDecimals(value, 6);
      setAmount(truncated);
    }
  };

  /**
   * Set amount to maximum balance
   * @param maxBalance - Maximum available balance
   */
  const setMaxAmount = (maxBalance: string) => {
    setAmount(maxBalance);
  };

  return {
    sourceChainId,
    setSourceChainId,
    amount,
    setAmount: setValidatedAmount,
    setMaxAmount,
    txHash,
    setTxHash,
    clearForms,
  };
};
